import type { Building } from "building-types/building/buildingTypes";
import type { ControllerPlacementCandidateTable } from "building-types/electrical/candidate";
import type { ElectricalController, ElectricalLoad } from "building-types/electrical/electricalDeviceTypes";
import { ElectricalDeviceType } from "building-types/electrical/electricalDeviceTypesEnum";
import type { InPlaceElectricalLayout } from "building-types/electrical/electricalInPlaceLayoutTypes";
import type { ElectricalSystemView } from "building-types/electrical/electricalViewTypes";
import type { GeneratedElectricalGroup } from "building-types/electrical/generatedElectricalTypes";
import type { ElectricalHandle, ElectricalRuleHandle } from "building-types/handles/docEntityHandleTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import { type BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { evalFormulas, evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { deriveElectricalSettingsProvenance } from "doc/generate/deriveElectricalSettingsProvenance";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { mergedPropLibraryNew } from "doc/propTools";
import { uniqueRoomSetsTables } from "doc/rooms/uniqueRoomSetsTables";
import { makeElectricalSystemView } from "doc/utils/electricalViewUtils";
import type { Vec2 } from "geom/vec2";
import { candidatesCornerNew } from "../elements/electrical/controllerCandidates/candidatesCorner";
import { controllerSelectionExplicitFromRoomBlockDoc } from "../elements/electrical/controllerCandidates/candidatesExplicit";
import { candidatesOpeningNew } from "../elements/electrical/controllerCandidates/electricalCandidatesTraverse";
import { controllerSelectionFuncNew } from "../elements/electrical/controllerSelection/controllerSelection";
import { deriveControllerCandidatePlacements } from "../elements/electrical/controllerSelection/deriveControllerCandidatePlacements";
import { roomSetInfoTableNew } from "../elements/electrical/electricalRoomSetInfo";
import { electricalGroupsFromRoomBlockDoc } from "../elements/electrical/stages/electricalGroupsFromRoomBlockDoc";
import { electricalGroupsFromRoomElectricalRules } from "../elements/electrical/stages/electricalGroupsFromRoomElectricalRules";
import { electricalLoadPrePassForControllerSelectionFromRoomBlockDoc } from "../elements/electrical/stages/electricalLoadPrePassForControllerSelectionFromRoomBlockDoc";
import { collectElectricalSettingsGenerationIssues } from "../generationTools/generateElectricalTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import { memoAsync } from "../utils/memo";

export function selectElectrical({
  levels,
  rooms,
  roomsByLevel,
  roomIndex,
  wallMasses,
  walls,
  doorPlacements: doors,
  settings,
  roomLayoutElectrical,
  doc,
  props,
  casework,
  optionChoices,
  roomOutlineTable,
  mirror,
  planarGraphs,
  entityDisabledByGroup,
  referenceGeom,
  enabledFeatures,
}: Pick<
  Building,
  | "levels"
  | "rooms"
  | "roomsByLevel"
  | "roomIndex"
  | "wallMasses"
  | "walls"
  | "doorPlacements"
  | "settings"
  | "roomLayoutElectrical"
  | "doc"
  | "props"
  | "casework"
  | "optionChoices"
  | "roomOutlineTable"
  | "mirror"
  | "planarGraphs"
  | "entityDisabledByGroup"
  | "referenceGeom"
  | "enabledFeatures"
>) {
  const definitions = docToOptionDefinitions(doc);
  return {
    levels,
    rooms,
    roomsByLevel,
    roomIndex,
    wallMasses,
    electricalSettings: settings.electrical,
    electricalItemCatalogSettings: settings.electricalItemCatalog,
    propCatalog: settings.props,
    profileSettings: settings.profile,
    profiles: doc.profiles,
    settingsToCatalogs: enabledFeatures.settingsToCatalogs,
    docEntityGroups: doc.entityGroups,
    walls,
    doors,
    docElectricalDevices: doc.electricalDevices,
    docElectricalLayouts: doc.electricalLayouts,
    docRooms: doc.rooms,
    referenceGeom,
    optionChoices: resolveOptionsInFormula(doc.electricalDevices, doc, optionChoices),
    layoutOptionChoices: resolveOptionsInFormula(doc.electricalLayouts, doc, optionChoices),
    rawOptionChoices: optionChoices,
    definitions,
    roomLayoutElectrical,
    props,
    casework,
    roomOutlineTable,
    mirror,
    planarGraphs,
    doorSettings: settings.door,
    entityDisabledByGroup,
    docSettings: doc.settings,
    docSettingsSets: doc.settingsSets,
    docOptionChoices: doc.optionChoices,
    docOptionSets: doc.optionSets,
    assets: doc.assets,
  };
}

export const compareElectricalSelectParams = makeGenerationStageComparator<typeof selectElectrical>({
  optionChoices: "shallow",
  layoutOptionChoices: "shallow",
  definitions: "shallow", // wrapper object is rebuilt each select(); its optionSets refs are stable
});

export async function generateElectrical(
  {
    levels,
    rooms,
    roomsByLevel,
    roomIndex,
    wallMasses,
    electricalSettings,
    electricalItemCatalogSettings,
    propCatalog,
    walls: wallSystems,
    doors,
    referenceGeom,
    roomLayoutElectrical,
    props,
    casework,
    optionChoices,
    layoutOptionChoices,
    rawOptionChoices,
    definitions,
    roomOutlineTable: roomOutlines,
    mirror,
    planarGraphs,
    profileSettings,
    profiles,
    settingsToCatalogs,
    doorSettings,
    docElectricalDevices,
    docElectricalLayouts,
    entityDisabledByGroup,
    docEntityGroups,
    docRooms,
    docSettings,
    docSettingsSets,
    docOptionChoices,
    docOptionSets,
    assets,
  }: ReturnType<typeof selectElectrical>,
  forEveryElectrical: ((handle: ElectricalHandle, pos: Vec2) => void) | null = null,
): Promise<{
  electrical: GeneratedElectricalGroup[];
  electricalRulesByRoom: Table<readonly ElectricalRuleHandle[]>;
  electricalControllerCandidates: ControllerPlacementCandidateTable;
  electricalSystemView: ElectricalSystemView;
  issues: BuildingGenerationIssue[];
}> {
  const electricalSettingsProvenance = deriveElectricalSettingsProvenance({
    settings: docSettings,
    settingsSets: docSettingsSets,
    optionChoices: docOptionChoices,
    optionSets: docOptionSets,
  });

  const evaluatedArchetypeElectricalDevices = evalFormulas(
    docElectricalDevices,
    (id) => optionChoices[id],
    undefined,
    entityDisabledByGroup,
  );

  const evaluatedArchetypeElectricalLayouts = evalFormulas(
    docElectricalLayouts,
    (id) => layoutOptionChoices[id],
    undefined,
    entityDisabledByGroup,
  );

  const issues: BuildingGenerationIssue[] = collectElectricalSettingsGenerationIssues(docSettings);

  /**
   * TODO:-move this block to generateElectricalTools
   */
  const includeDisabledEntities = forEveryElectrical !== null;
  const { electricalLoads, electricalControllers, electricalLayouts, electricalEntityGroups } =
    instantiateElectricalEntitiesForRooms({
      rawElectricalDevices: docElectricalDevices,
      rawElectricalLayouts: docElectricalLayouts,
      evaluatedArchetypeElectricalDevices,
      evaluatedArchetypeElectricalLayouts,
      rooms,
      definitions,
      rawOptionChoices,
      includeDisabledEntities,
      entityGroups: docEntityGroups,
    });

  const wallLayerExtentsCache: WallLayerExtentsCache = {};
  const mergedPropLibrary = mergedPropLibraryNew(assets);

  const { slots } = electricalSettings;

  const { roomSets, roomSetsByLevelId, roomIdToRoomSetIndexLookup } = uniqueRoomSetsTables(levels, wallMasses);
  const roomSetInfoTable = roomSetInfoTableNew(roomSets, doors, rooms, roomIdToRoomSetIndexLookup);

  // gather controller location candidates from outside corners.
  const controllerPlacementCandidates = candidatesCornerNew(
    levels,
    wallSystems,
    rooms,
    roomsByLevel,
    roomSets,
    roomIdToRoomSetIndexLookup,
  );

  // gather controller location candidates by traversing each way from suitable doors
  candidatesOpeningNew({
    doors,
    planarGraphs,
    wallSystems,
    wallMassSystems: wallMasses,
    profileSource: { settingsToCatalogs, profiles, profileSettings },
    doorSettings,
    roomIdToRoomSetIndexLookup,
    candidateTable: controllerPlacementCandidates,
  });

  // creates controller selection funcs so that each electrical group can pull from the same list of controllers and share explicitly created controllers
  const { controllerSelectionFunc, explicitControllerSelectionFunc, updateControllerLookupsForNewIdsAndTags } =
    controllerSelectionFuncNew(
      roomIdToRoomSetIndexLookup,
      controllerPlacementCandidates,
      roomSetInfoTable,
      rooms,
      wallSystems,
      referenceGeom,
      props,
      casework,
      propCatalog,
      mergedPropLibrary,
      mirror,
      roomOutlines,
      wallMasses,
      roomIndex,
      wallLayerExtentsCache,
    );

  controllerSelectionExplicitFromRoomBlockDoc({
    roomLayoutElectrical,
    explicitControllerSelectionFunc,
    electricalControllers,
  });

  updateControllerLookupsForNewIdsAndTags();

  await electricalLoadPrePassForControllerSelectionFromRoomBlockDoc({
    roomLayoutElectrical,
    controllerSelectionFunc,
    props,
    propCatalog,
    mergedPropLibrary,
    electricalItemCatalogSettings,
    roomIdToRoomSetIndexLookup,
    roomSetInfoTable,
    roomTable: rooms,
    slots,
    roomOutlines,
    mirror,
    electricalControllers,
  });

  updateControllerLookupsForNewIdsAndTags();

  const roomDocBlockElectricalResult = await electricalGroupsFromRoomBlockDoc({
    legacyRoomLayoutElectrical: roomLayoutElectrical,
    controllerSelectionFunc,
    roomTable: rooms,
    props,
    casework,
    referenceGeom,
    propCatalog,
    mergedPropLibrary,
    electricalItemCatalogSettings,
    roomIdToRoomSetIndexLookup,
    roomSetInfoTable,
    slots,
    mirror,
    roomOutlines,
    wallSystems,
    candidateTable: controllerPlacementCandidates,
    roomSets,
    wallMasses,
    roomIndex,
    wallLayerExtentsCache,
    electricalControllers,
    electricalLoads,
    entityGroups: electricalEntityGroups,
    electricalLayouts,
  });

  updateControllerLookupsForNewIdsAndTags();

  const { electrical, electricalRulesByRoom } = await electricalGroupsFromRoomElectricalRules(
    levels,
    rooms,
    controllerPlacementCandidates,
    roomSetsByLevelId,
    wallSystems,
    electricalSettings,
    electricalItemCatalogSettings,
    roomDocBlockElectricalResult,
    props,
    controllerSelectionFunc,
    roomSetInfoTable,
    propCatalog,
    mergedPropLibrary,
    slots,
    mirror,
    roomOutlines,
    docRooms,
    electricalSettingsProvenance,
  );

  // adjusts the positions of e.g. switches at a controller location
  deriveControllerCandidatePlacements(controllerPlacementCandidates, mirror);

  const electricalSystemView = makeElectricalSystemView(
    electrical,
    controllerPlacementCandidates,
    levels,
    rooms,
    mirror,
  );

  if (forEveryElectrical) {
    Object.values(electricalSystemView).forEach((esv) => {
      for (const l of esv.loads) {
        if (l.handle) {
          forEveryElectrical(l.handle, [l.positionX, l.positionY]);
        }
      }
      for (const c of esv.controllers) {
        if (c.handle) {
          forEveryElectrical(c.handle, [c.positionX, c.positionY]);
        }
      }
    });
  }

  return {
    electrical,
    electricalRulesByRoom,
    electricalControllerCandidates: controllerPlacementCandidates,
    electricalSystemView,
    issues,
  };
}

interface ElectricalRoomForInstantiation {
  readonly id: string;
  readonly sourceRoomId?: string;
  readonly componentTransform?: {
    readonly componentInstanceChain: readonly string[];
  };
}

interface InstantiateElectricalEntitiesForRoomsParams {
  readonly rawElectricalDevices: RWTable<ElectricalLoad | ElectricalController>;
  readonly rawElectricalLayouts: RWTable<InPlaceElectricalLayout>;
  readonly evaluatedArchetypeElectricalDevices: RWTable<ElectricalLoad | ElectricalController>;
  readonly evaluatedArchetypeElectricalLayouts: RWTable<InPlaceElectricalLayout>;
  readonly rooms: Readonly<Record<string, ElectricalRoomForInstantiation>>;
  readonly definitions: OptionDefinitions;
  readonly rawOptionChoices: OptionChoices;
  readonly includeDisabledEntities: boolean;
  readonly entityGroups: Readonly<Record<string, string>>;
}

interface InstantiatedElectricalEntities {
  readonly electricalLoads: RWTable<ElectricalLoad>;
  readonly electricalControllers: RWTable<ElectricalController>;
  readonly electricalLayouts: RWTable<InPlaceElectricalLayout>;
  readonly electricalEntityGroups: RWTable<string>;
}

function deviceGroupKeyForChain(sourceGroupId: string, componentInstanceChain: readonly string[] | undefined): string {
  if (!componentInstanceChain || componentInstanceChain.length === 0) {
    return sourceGroupId;
  }
  return `${sourceGroupId}::${componentInstanceChain.join("/")}`;
}

interface RoomInstantiationContext {
  readonly generatedRoomId: string;
  readonly componentInstanceChain: readonly string[] | undefined;
}

// Groups source-entity ids by their parent (source) room id.
function sourceRoomIdsByEntity<T extends { id: string; parentId?: string }>(
  entities: RWTable<T>,
): Record<string, string[]> {
  const idsBySourceRoom: Record<string, string[]> = {};
  for (const entity of Object.values(entities)) {
    if (!entity.parentId) {
      continue;
    }
    idsBySourceRoom[entity.parentId] ||= [];
    idsBySourceRoom[entity.parentId].push(entity.id);
  }
  return idsBySourceRoom;
}

// Instantiates one room's electrical loads, controllers, and entity-group assignments, expanding a
// component-instance source into a per-instance entity.
function instantiateElectricalDevicesForRoom(
  { generatedRoomId, componentInstanceChain }: RoomInstantiationContext,
  sourceDeviceIds: string[],
  {
    rawElectricalDevices,
    evaluatedArchetypeElectricalDevices,
    definitions,
    rawOptionChoices,
    includeDisabledEntities,
    entityGroups,
  }: InstantiateElectricalEntitiesForRoomsParams,
  result: InstantiatedElectricalEntities,
): void {
  const hasComponentChain = !!componentInstanceChain && componentInstanceChain.length > 0;
  for (const sourceDeviceId of sourceDeviceIds) {
    const rawDevice = rawElectricalDevices[sourceDeviceId];
    if (!rawDevice) {
      continue;
    }
    const evaluatedDevice = hasComponentChain
      ? evaluateEntityForComponentInstance(rawDevice, componentInstanceChain, definitions, rawOptionChoices)
      : evaluatedArchetypeElectricalDevices[sourceDeviceId];
    if (!evaluatedDevice) {
      continue;
    }
    if (evaluatedDevice.disabled && !includeDisabledEntities) {
      continue;
    }

    const instantiatedDeviceId = hasComponentChain
      ? instanceIdForComponent(sourceDeviceId, componentInstanceChain)
      : sourceDeviceId;
    const instantiatedDevice = {
      ...evaluatedDevice,
      id: instantiatedDeviceId,
      parentId: generatedRoomId,
    };

    const sourceGroupId = entityGroups[docEntityHandleHash({ type: "ELECTRICAL_DEVICE", id: sourceDeviceId })];
    if (sourceGroupId) {
      const groupKey = deviceGroupKeyForChain(sourceGroupId, componentInstanceChain);
      result.electricalEntityGroups[docEntityHandleHash({ type: "ELECTRICAL_DEVICE", id: instantiatedDeviceId })] =
        groupKey;
    }

    switch (instantiatedDevice.deviceType) {
      case ElectricalDeviceType.LOAD:
        result.electricalLoads[instantiatedDeviceId] = instantiatedDevice;
        break;
      case ElectricalDeviceType.CONTROLLER:
        result.electricalControllers[instantiatedDeviceId] = instantiatedDevice;
        break;
    }
  }
}

// Instantiates one room's electrical layouts, expanding a component-instance source into a
// per-instance layout.
function instantiateElectricalLayoutsForRoom(
  { generatedRoomId, componentInstanceChain }: RoomInstantiationContext,
  sourceLayoutIds: string[],
  {
    rawElectricalLayouts,
    evaluatedArchetypeElectricalLayouts,
    definitions,
    rawOptionChoices,
    includeDisabledEntities,
  }: InstantiateElectricalEntitiesForRoomsParams,
  result: InstantiatedElectricalEntities,
): void {
  const hasComponentChain = !!componentInstanceChain && componentInstanceChain.length > 0;
  for (const sourceLayoutId of sourceLayoutIds) {
    const rawLayout = rawElectricalLayouts[sourceLayoutId];
    if (!rawLayout) {
      continue;
    }
    const evaluatedLayout = hasComponentChain
      ? evaluateEntityForComponentInstance(rawLayout, componentInstanceChain, definitions, rawOptionChoices)
      : evaluatedArchetypeElectricalLayouts[sourceLayoutId];
    if (!evaluatedLayout) {
      continue;
    }
    if (evaluatedLayout.disabled && !includeDisabledEntities) {
      continue;
    }

    const instantiatedLayoutId = hasComponentChain
      ? instanceIdForComponent(sourceLayoutId, componentInstanceChain)
      : sourceLayoutId;
    result.electricalLayouts[instantiatedLayoutId] = {
      ...evaluatedLayout,
      id: instantiatedLayoutId,
      parentId: generatedRoomId,
    };
  }
}

export function instantiateElectricalEntitiesForRooms(
  params: InstantiateElectricalEntitiesForRoomsParams,
): InstantiatedElectricalEntities {
  const { rooms, rawElectricalDevices, rawElectricalLayouts, entityGroups } = params;
  const result: InstantiatedElectricalEntities = {
    electricalLoads: {},
    electricalControllers: {},
    electricalLayouts: {},
    electricalEntityGroups: { ...entityGroups },
  };

  const deviceIdsBySourceRoom = sourceRoomIdsByEntity(rawElectricalDevices);
  const layoutIdsBySourceRoom = sourceRoomIdsByEntity(rawElectricalLayouts);

  for (const generatedRoomId in rooms) {
    const generatedRoom = rooms[generatedRoomId];
    const sourceRoomId = generatedRoom.sourceRoomId ?? generatedRoom.id;
    const context: RoomInstantiationContext = {
      generatedRoomId,
      componentInstanceChain: generatedRoom.componentTransform?.componentInstanceChain,
    };

    instantiateElectricalDevicesForRoom(context, deviceIdsBySourceRoom[sourceRoomId] ?? [], params, result);
    instantiateElectricalLayoutsForRoom(context, layoutIdsBySourceRoom[sourceRoomId] ?? [], params, result);
  }

  return result;
}

export default memoAsync(selectElectrical, generateElectrical, compareElectricalSelectParams);
